import {
    AggregatedUserExternalRewardsPnlResponse,
    BsMetaUtil,
    getMintsFromExternalRewards,
    useOraclePrices
} from "@bridgesplit/abf-react";
import { Column, StatColumn, StatProps, Text } from "@bridgesplit/ui";
import { TokenAmountWithUsd } from "app/components/common";

import { useVaultContext } from "../VaultContext";

const DEFAULT_STAT_ROWS = 2;

const SKELETON_STATS: StatProps[] = Array(DEFAULT_STAT_ROWS).fill({
    value: null,
    caption: null,
    hide: false
});

export function hasExternalRewardsStats(
    externalRewards: AggregatedUserExternalRewardsPnlResponse | null | undefined
): boolean {
    if (!externalRewards) return false;

    // Only show stat if user has pending rewards
    // or claimed (with no pending) rewards that are NOT zero
    return Object.values(externalRewards).some((vaultRewards) =>
        Object.values(vaultRewards).some(
            (reward) => reward.pendingRewards > 0 || (reward.claimedRewards > 0 && !reward.pendingRewards)
        )
    );
}
export function VaultExternalRewardsHeader() {
    const { userExternalRewardsPosition } = useVaultContext();

    const mints = getMintsFromExternalRewards(userExternalRewardsPosition);

    const { getUsdPrice } = useOraclePrices(mints);

    const stats = getVaultExternalRewardsStats({
        externalRewards: userExternalRewardsPosition,
        getUsdPrice
    });

    if (userExternalRewardsPosition && !hasExternalRewardsStats(userExternalRewardsPosition)) {
        return null;
    }

    return (
        <Column spacing={2}>
            <Text variant="h4">Rewards</Text>
            <StatColumn stats={stats ?? SKELETON_STATS} loading={!userExternalRewardsPosition} />
        </Column>
    );
}

function getVaultExternalRewardsStats({
    externalRewards,
    getUsdPrice
}: {
    externalRewards: AggregatedUserExternalRewardsPnlResponse | null | undefined;
    getUsdPrice: (mint: string) => number | undefined;
}): StatProps[] | undefined {
    if (!externalRewards) return undefined;

    const stats: StatProps[] = [];

    // Iterate through each vault's rewards
    Object.values(externalRewards).forEach((vaultRewards) => {
        Object.values(vaultRewards).forEach((reward) => {
            const usdPrice = getUsdPrice(reward.rewardMint);
            const symbol = BsMetaUtil.getSymbol(reward.rewardMetadata);

            if (reward.claimedRewards === 0) {
                return;
            }

            const stat: StatProps = {
                value: (
                    <TokenAmountWithUsd
                        amount={reward.claimedRewards}
                        token={reward.rewardMetadata}
                        condensedOnMobile
                        amountUsd={reward.claimedRewards * (usdPrice ?? 0)}
                    />
                ),
                caption: `Total ${symbol} claimed`
            };

            stats.push(stat);
        });
    });

    return stats;
}
